import { useState } from "react";
import { setConsent } from "../utils/consent";
import "./ConsentScreen.css";

interface ConsentScreenProps {
  onAccept: () => void;
}

function ConsentScreen({ onAccept }: ConsentScreenProps) {
  const [isScrolled, setIsScrolled] = useState(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrollPercentage =
      (target.scrollTop / (target.scrollHeight - target.clientHeight)) * 100;
    setIsScrolled(scrollPercentage > 80); // Разрешаем кнопку после 80% прокрутки
  };

  const handleAccept = () => {
    setConsent(true);
    onAccept();
  };

  return (
    <div className="consent-screen">
      <div className="consent-container">
        <div className="consent-header">
          <h1>Политика конфиденциальности</h1>
          <p className="consent-subtitle">
            Пожалуйста, ознакомьтесь с условиями использования сервиса
          </p>
        </div>

        <div className="consent-content" onScroll={handleScroll}>
          <div className="consent-text">
            <h2>1. Общие положения</h2>
            <p>
              Настоящая Политика конфиденциальности описывает порядок сбора,
              хранения, обработки и защиты персональных данных пользователей
              Telegram MiniApp «ФотАй».
            </p>
            <p>
              Используя Сервис, вы соглашаетесь с условиями настоящей Политики.
              Если вы не согласны с условиями, пожалуйста, не используйте
              Сервис.
            </p>

            <h2>2. Какие данные мы собираем</h2>
            <h3>2.1. Данные, предоставляемые пользователем</h3>
            <ul>
              <li>
                <strong>Изображения</strong>: загружаемые вами фотографии и
                изображения в форматах JPG, JPEG, PNG, WEBP
              </li>
              <li>
                <strong>Метаданные изображений</strong>: оригинальное имя файла
                (используется только для определения расширения), размер файла,
                размеры изображения
              </li>
              <li>
                <strong>ID пользователя Telegram</strong>: опциональный
                идентификатор, передаваемый при использовании функции генерации
                фотосессий
              </li>
            </ul>

            <h2>3. Цели обработки данных</h2>
            <p>Мы обрабатываем ваши данные исключительно для:</p>
            <ul>
              <li>Предоставления функционала Сервиса</li>
              <li>Обеспечения работоспособности и безопасности</li>
              <li>Улучшения качества предоставляемых услуг</li>
              <li>Технической поддержки и устранения ошибок</li>
            </ul>

            <h2>4. Способы обработки данных</h2>
            <h3>4.1. Хранение данных</h3>
            <ul>
              <li>
                Загруженные изображения временно сохраняются на сервере с
                уникальными именами файлов
              </li>
              <li>Временные файлы автоматически удаляются после обработки</li>
              <li>
                Обработанные файлы могут быть доступны через публичные URL для
                обеспечения функционала Сервиса
              </li>
            </ul>

            <h3>4.2. Передача данных третьим лицам</h3>
            <p>
              Для предоставления функционала ваши изображения могут передаваться
              сторонним сервисам:
            </p>
            <ul>
              <li>
                <strong>Yes AI API</strong>: для генерации фотосессий по
                референсным изображениям
              </li>
              <li>
                <strong>Remove.bg API</strong>: для удаления фона с изображений
              </li>
            </ul>
            <p>
              <strong>Важно</strong>: Мы не контролируем политику
              конфиденциальности сторонних сервисов. Рекомендуем ознакомиться с
              их политиками конфиденциальности.
            </p>

            <h2>5. Сроки хранения данных</h2>
            <ul>
              <li>
                Временные файлы автоматически удаляются сразу после обработки
              </li>
              <li>
                Загруженные файлы могут храниться на сервере до тех пор, пока
                они необходимы для обеспечения функционала
              </li>
              <li>Логи хранятся не более 90 дней</li>
            </ul>

            <h2>6. Меры защиты данных</h2>
            <ul>
              <li>Ограничение размера загружаемых файлов (максимум 10 МБ)</li>
              <li>Валидация форматов файлов</li>
              <li>Генерация уникальных имен файлов</li>
              <li>
                Использование защищенных протоколов передачи данных (HTTPS)
              </li>
            </ul>
            <p className="warning">
              <strong>Важно</strong>: Публичные URL к загруженным файлам могут
              быть доступны любому, кто знает имя файла. Мы рекомендуем не
              загружать конфиденциальные или личные изображения, если вы не
              готовы к их публичному доступу.
            </p>

            <h2>7. Права пользователей</h2>
            <p>Вы имеете право:</p>
            <ul>
              <li>
                Запросить информацию о том, какие ваши данные обрабатываются
              </li>
              <li>Запросить исправление неточных данных</li>
              <li>Запросить удаление ваших данных</li>
              <li>Запросить ограничение обработки ваших данных</li>
              <li>Возразить против обработки ваших данных</li>
            </ul>

            <h2>8. Возрастные ограничения</h2>
            <p>
              Сервис предназначен для пользователей старше 13 лет. Если вам
              меньше 13 лет, пожалуйста, не используйте Сервис без согласия
              родителей или опекунов.
            </p>

            <h2>9. Изменения в Политике</h2>
            <p>
              Мы оставляем за собой право вносить изменения в настоящую Политику
              конфиденциальности. О существенных изменениях мы уведомим
              пользователей через обновление даты «Последнего обновления».
            </p>

            <div className="consent-footer-text">
              <p>
                Полная версия политики конфиденциальности доступна в репозитории
                проекта.
              </p>
              <p className="consent-date">
                Дата последнего обновления: 19 декабря 2024
              </p>
            </div>
          </div>
        </div>

        <div className="consent-actions">
          <div className="consent-scroll-indicator">
            {!isScrolled && (
              <p className="scroll-hint">
                Прокрутите вниз, чтобы прочитать все условия
              </p>
            )}
          </div>
          <button
            className={`consent-button ${isScrolled ? "enabled" : "disabled"}`}
            onClick={handleAccept}
            disabled={!isScrolled}
          >
            Принимаю условия
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConsentScreen;
